"use client";
import React, { useEffect, useRef } from "react";
import createGlobe from "cobe";
import { cn } from "../../lib/utils";

const hexToRgbNormalized = (hex: string): [number, number, number] => {
  let r = 0,
    g = 0,
    b = 0;

  const cleanHex = hex.startsWith("#") ? hex.slice(1) : hex;

  if (cleanHex.length === 3) {
    r = parseInt(cleanHex[0] + cleanHex[0], 16);
    g = parseInt(cleanHex[1] + cleanHex[1], 16);
    b = parseInt(cleanHex[2] + cleanHex[2], 16);
  } else if (cleanHex.length === 6) {
    r = parseInt(cleanHex.substring(0, 2), 16);
    g = parseInt(cleanHex.substring(2, 4), 16);
    b = parseInt(cleanHex.substring(4, 6), 16);
  } else {
    console.warn(`Invalid hex color: ${hex}. Falling back to black.`);
    return [0, 0, 0];
  }

  return [r / 255, g / 255, b / 255];
};

interface GlobeProps {
  className?: string;
  theta?: number;
  dark?: number;
  scale?: number;
  diffuse?: number;
  mapSamples?: number;
  mapBrightness?: number;
  baseColor?: [number, number, number] | string;
  markerColor?: [number, number, number] | string;
  glowColor?: [number, number, number] | string;
  size?: "sm" | "md" | "lg" | "xl" | "full"; // New prop for size control
}

const Globe: React.FC<GlobeProps> = ({
  className,
  theta = 0.25,
  dark = 0,
  scale = 1.8, // Increased scale for bigger globe
  diffuse = 1.6, // Increased for better lighting
  mapSamples = 80000, // Increased for more detail
  mapBrightness = 12, // Increased brightness
  baseColor = "#6C63FF",
  markerColor = "#8B7CFF",
  glowColor = "#6C63FF",
  size = "xl", // Default to extra large
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const globeRef = useRef<any>(null);

  const phiRef = useRef(0);
  const thetaRef = useRef(theta);
  const isDragging = useRef(false);
  const lastMouseX = useRef(0);
  const lastMouseY = useRef(0);
  const autoRotateSpeed = 0.002; // Slower auto rotation

  // Size mapping
  const sizeMap = {
    sm: "20rem",
    md: "30rem",
    lg: "40rem",
    xl: "60rem",
    full: "100vw",
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resolvedBaseColor: [number, number, number] =
      typeof baseColor === "string"
        ? hexToRgbNormalized(baseColor)
        : baseColor;

    const resolvedMarkerColor: [number, number, number] =
      typeof markerColor === "string"
        ? hexToRgbNormalized(markerColor)
        : markerColor;

    const resolvedGlowColor: [number, number, number] =
      typeof glowColor === "string"
        ? hexToRgbNormalized(glowColor)
        : glowColor;

    const initGlobe = () => {
      if (globeRef.current) {
        globeRef.current.destroy();
        globeRef.current = null;
      }

      const rect = containerRef.current?.getBoundingClientRect() || canvas.getBoundingClientRect();
      const sizeValue = rect.width;
      const devicePixelRatio = window.devicePixelRatio || 1;
      const internalWidth = sizeValue * devicePixelRatio;
      const internalHeight = sizeValue * devicePixelRatio;

      canvas.width = internalWidth;
      canvas.height = internalHeight;

      globeRef.current = createGlobe(canvas, {
        devicePixelRatio: devicePixelRatio,
        width: internalWidth,
        height: internalHeight,
        phi: phiRef.current,
        theta: thetaRef.current,
        dark: dark,
        scale: scale,
        diffuse: diffuse,
        mapSamples: mapSamples,
        mapBrightness: mapBrightness,
        baseColor: resolvedBaseColor,
        markerColor: resolvedMarkerColor,
        glowColor: resolvedGlowColor,
        opacity: 0.95, // Increased opacity for more presence
        offset: [0, 0],
        markers: [],
        onRender: (state: Record<string, any>) => {
          if (!isDragging.current) {
            phiRef.current += autoRotateSpeed;
          }
          state.phi = phiRef.current;
          state.theta = thetaRef.current;
          
          // Enhanced glow effect
          state.glowColor = resolvedGlowColor;
          state.glowPower = isDragging.current ? 12 : 8;
          state.glowCoefficient = 0.4;
        },
      });
    };

    const onMouseDown = (e: MouseEvent) => {
      isDragging.current = true;
      lastMouseX.current = e.clientX;
      lastMouseY.current = e.clientY;
      canvas.style.cursor = "grabbing";
    };

    const onMouseMove = (e: MouseEvent) => {
      if (isDragging.current) {
        const deltaX = e.clientX - lastMouseX.current;
        const deltaY = e.clientY - lastMouseY.current;
        const rotationSpeed = 0.003; // Smoother rotation

        phiRef.current += deltaX * rotationSpeed;
        thetaRef.current = Math.max(
          -Math.PI / 2.5,
          Math.min(Math.PI / 2.5, thetaRef.current - deltaY * rotationSpeed)
        );

        lastMouseX.current = e.clientX;
        lastMouseY.current = e.clientY;
      }
    };

    const onMouseUp = () => {
      isDragging.current = false;
      canvas.style.cursor = "grab";
    };

    const onMouseLeave = () => {
      if (isDragging.current) {
        isDragging.current = false;
        canvas.style.cursor = "grab";
      }
    };

    initGlobe();

    canvas.addEventListener("mousedown", onMouseDown);
    canvas.addEventListener("mousemove", onMouseMove);
    canvas.addEventListener("mouseup", onMouseUp);
    canvas.addEventListener("mouseleave", onMouseLeave);

    const handleResize = () => {
      initGlobe();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (canvas) {
        canvas.removeEventListener("mousedown", onMouseDown);
        canvas.removeEventListener("mousemove", onMouseMove);
        canvas.removeEventListener("mouseup", onMouseUp);
        canvas.removeEventListener("mouseleave", onMouseLeave);
      }
      if (globeRef.current) {
        globeRef.current.destroy();
        globeRef.current = null;
      }
    };
  }, [
    theta,
    dark,
    scale,
    diffuse,
    mapSamples,
    mapBrightness,
    baseColor,
    markerColor,
    glowColor,
    size, // Added size to dependencies
  ]);

  return (
    <div
      ref={containerRef}
      className={cn(
        "relative z-0 mx-auto overflow-visible",
        className
      )}
      style={{
        width: size === "full" ? "100vw" : sizeMap[size],
        height: size === "full" ? "100vh" : sizeMap[size],
        maxWidth: "100vw",
        maxHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        pointerEvents: "none", // Allow interaction through container
      }}
    >
      {/* Enhanced glow effect behind globe */}
      <div 
        className="absolute inset-0 z-[-1]"
        style={{
          background: `radial-gradient(circle at center, 
            ${baseColor}10 0%, 
            ${baseColor}05 40%, 
            transparent 70%
          )`,
          filter: "blur(40px)",
        }}
      />
      
      {/* Secondary glow for depth */}
      <div 
        className="absolute inset-[-50%] z-[-2]"
        style={{
          background: `radial-gradient(circle at center, 
            ${markerColor}15 0%, 
            ${markerColor}05 30%, 
            transparent 60%
          )`,
          filter: "blur(60px)",
          animation: "pulse 4s ease-in-out infinite alternate",
        }}
      />

      <canvas
        ref={canvasRef}
        className="absolute inset-0"
        style={{
          width: "100%",
          height: "100%",
          aspectRatio: "1",
          cursor: "grab",
          filter: "drop-shadow(0 0 60px rgba(108, 99, 255, 0.3))", // Enhanced shadow
          animation: "float 6s ease-in-out infinite", // Subtle floating animation
        }}
      />

      {/* Edge glow effect */}
      <div 
        className="absolute inset-0 rounded-full"
        style={{
          boxShadow: `
            inset 0 0 80px ${baseColor}20,
            inset 0 0 160px ${baseColor}10,
            0 0 120px ${baseColor}15
          `,
          pointerEvents: "none",
        }}
      />

      <style >{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px) scale(1);
          }
          50% {
            transform: translateY(-10px) scale(1.02);
          }
        }
        
        @keyframes pulse {
          0% {
            opacity: 0.3;
            transform: scale(0.95);
          }
          100% {
            opacity: 0.5;
            transform: scale(1.05);
          }
        }
      `}</style>
    </div>
  );
};

export default Globe;